import { CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
    ActivatedRoute,
    ChildrenOutletContexts,
    Router,
    RouterOutlet,
} from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoPipe, provideTranslocoScope } from '@ngneat/transloco';
import {
    Step,
    StepperService,
} from 'app/core/common/services/stepper/stepper.service';
import { DecorationService } from 'app/core/decorations/decoration.service';
import { DecorationConfig } from 'app/core/decorations/decoration.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { NavigationStepperComponent } from 'app/layout/common/stepper/stepper.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { GoodCircleCardComponent } from '../../../../modules/common/components/misc/components/good-circle-card/good-circle-card.component';

@Component({
    selector: 'app-onboarding-layout',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        RouterOutlet,
        NavigationStepperComponent,
        CdkStepperModule,
        TranslocoPipe,
        GoodCircleCardComponent,
    ],
    providers: [
        provideTranslocoScope('onboarding'),
        provideTranslocoScope('common'),
    ],
    animations: fuseAnimations,
})
export class OnboardingLayoutComponent implements OnInit, OnDestroy {
    @ViewChild('decorationComponent', { read: ViewContainerRef, static: false })
    set decorationComponentRef(ref: ViewContainerRef) {
        if (ref && this._decorationConfig) {
            const component = this._decorationConfig.component;

            if (component && ref) {
                const componentFactory =
                    this.componentFactoryResolver.resolveComponentFactory(
                        component
                    );
                ref.createComponent(componentFactory);
            }
        }
    }

    public layout$!: Observable<'full' | 'split'>;
    private _decorationConfig: DecorationConfig | null = null;

    isScreenSmall!: boolean;

    private _unsubscribeAll: Subject<null> = new Subject<null>();

    public steps$!: Observable<Step[] | null>;
    public index$!: Observable<number | null>;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _contexts: ChildrenOutletContexts,
        private _cdr: ChangeDetectorRef,
        private readonly _decorationService: DecorationService,
        private readonly _stepperService: StepperService,
        private readonly componentFactoryResolver: ComponentFactoryResolver
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.layout$ = this._decorationService.layout$;

        this._decorationService.config$
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap((config) => (this._decorationConfig = config))
            )
            .subscribe();

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this.steps$ = this._stepperService.steps$;
        this.index$ = this._stepperService.index$;

        this._router.events.subscribe(() => {
            let path = this._activatedRoute;

            while (path.firstChild !== null) path = path.firstChild;

            const paths = path.pathFromRoot;
            if (paths)
                paths.forEach((path) => {
                    if (
                        path.routeConfig &&
                        path.routeConfig.data &&
                        path.routeConfig.data['steps']
                    ) {
                        this._cdr.markForCheck();
                    }

                    if (
                        path.routeConfig &&
                        path.routeConfig.data &&
                        path.routeConfig.data['step']
                    ) {
                        this._cdr.markForCheck();
                    }
                });
        });

        // this._stepperService.steps = [
        //     {
        //         label: 'Données administratives',
        //         children: [
        //             {
        //                 label: 'Gouvernance',
        //                 children: [
        //                     {
        //                         label: 'Stratégie de développement durable',
        //                     },
        //                     {
        //                         label: 'Engagement social, local et solidaire',
        //                     },
        //                     {
        //                         label: 'Partage du pouvoir et de la valeur',
        //                     },
        //                     {
        //                         label: 'Engagement social, local et solidaire',
        //                     },
        //                     {
        //                         label: 'Partage du pouvoir et de la valeur',
        //                     },
        //                 ],
        //             },
        //             {
        //                 label: 'Gouvernance',
        //                 children: [],
        //             },
        //         ],
        //     },
        //     {
        //         label: "Information sur l'entreprise",
        //         children: [],
        //     },
        //     {
        //         label: 'Démarche durable',
        //         children: [],
        //     },
        //     {
        //         label: 'Diagnostic impact',
        //         children: [
        //             {
        //                 label: 'Gouvernance',
        //                 children: [
        //                     {
        //                         label: 'Stratégie de développement durable',
        //                     },
        //                     {
        //                         label: 'Engagement social, local et solidaire',
        //                     },
        //                     {
        //                         label: 'Partage du pouvoir et de la valeur',
        //                     },
        //                     {
        //                         label: 'Engagement social, local et solidaire',
        //                     },
        //                     {
        //                         label: 'Partage du pouvoir et de la valeur',
        //                     },
        //                 ],
        //             },
        //             {
        //                 label: 'Social',
        //                 children: [],
        //             },
        //             {
        //                 label: 'Environnement',
        //                 children: [],
        //             },
        //         ],
        //     },
        // ];

        // this._stepperService.index = 5;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    overallIndex(
        steps: Step[] | null,
        step: number | null,
        category?: number,
        section?: number
    ): number {
        let index = 0;

        if (!steps) return 0;

        for (let i = 0; i < steps.length; i++) {
            if (i === step && category === undefined) return index;

            for (let j = 0; j < steps[i].children.length; j++) {
                index++;

                if (i === step && j === category && section === undefined)
                    return index;

                for (let k = 0; k < steps[i].children[j].children.length; k++) {
                    index++;

                    if (i === step && j === category && k === section)
                        return index;
                }
            }
            index++;
        }

        return index;
    }

    // isStepSelected(
    //     step: number,
    //     category?: number,
    //     section?: number
    // ): 'PREVIOUS' | 'CURRENT' | 'NEXT' {
    //     let index = 0;
    //     let currentStepIndex = 0;
    //     let currentCategoryIndex = 0;
    //     let currentSectionIndex = 0;
    //     let stepIndex = 0;
    //     let categoryIndex = 0;
    //     let sectionIndex = 0;

    //     const overallIndex = this.stepIndex;

    //     for (let i = 0; i < this.steps.length; i++) {
    //         stepIndex = index;

    //         if (stepIndex <= overallIndex) {
    //             currentStepIndex = stepIndex;
    //         }

    //         for (let j = 0; j < this.steps[i].children.length; j++) {
    //             index++;
    //             categoryIndex = index;

    //             if (categoryIndex <= overallIndex) {
    //                 currentCategoryIndex = categoryIndex;
    //             }

    //             for (
    //                 let k = 0;
    //                 k < this.steps[i].children[j].children.length;
    //                 k++
    //             ) {
    //                 index++;
    //                 sectionIndex = index;

    //                 if (sectionIndex <= overallIndex)
    //                     currentSectionIndex = sectionIndex;
    //             }
    //         }
    //         index++;
    //     }

    //     index = 0;
    //     stepIndex = 0;
    //     categoryIndex = 0;
    //     sectionIndex = 0;

    //     for (let i = 0; i < this.steps.length; i++) {
    //         stepIndex = index;

    //         if (i === step && category === undefined) {
    //             return currentStepIndex < stepIndex
    //                 ? 'NEXT'
    //                 : currentStepIndex > stepIndex
    //                     ? 'PREVIOUS'
    //                     : 'CURRENT';
    //         }

    //         for (let j = 0; j < this.steps[i].children.length; j++) {
    //             index++;
    //             categoryIndex = index;

    //             if (i === step && j === category && section === undefined) {
    //                 return currentCategoryIndex < categoryIndex
    //                     ? 'NEXT'
    //                     : currentCategoryIndex > categoryIndex
    //                         ? 'PREVIOUS'
    //                         : 'CURRENT';
    //             }

    //             for (
    //                 let k = 0;
    //                 k < this.steps[i].children[j].children.length;
    //                 k++
    //             ) {
    //                 index++;
    //                 sectionIndex = index;

    //                 if (i === step && j === category && k === section) {
    //                     return currentSectionIndex < sectionIndex
    //                         ? 'NEXT'
    //                         : currentSectionIndex > sectionIndex
    //                             ? 'PREVIOUS'
    //                             : 'CURRENT';
    //                 }
    //             }
    //         }
    //         index++;
    //     }

    //     return 'NEXT';
    // }
    isStepSelected(
        steps: Step[] | null,
        overallIndex: number | null,
        step: number,
        category?: number,
        section?: number
    ): 'PREVIOUS' | 'CURRENT' | 'NEXT' {
        const currentIndexes = { step: 0, category: 0, section: 0 };
        let index = 0;

        if (!steps || overallIndex === null) return 'NEXT';
        // const overallIndex = this.stepIndex;

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < steps.length; i++) {
            if (index <= overallIndex) currentIndexes.step = index;

            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let j = 0; j < steps[i].children.length; j++) {
                index++;
                if (index <= overallIndex) currentIndexes.category = index;

                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let k = 0; k < steps[i].children[j].children.length; k++) {
                    index++;
                    if (index <= overallIndex) currentIndexes.section = index;
                }
            }
            index++;
        }

        index = 0;

        // Helper function to determine the status
        const getStatus = (current: number, target: number) =>
            current < target
                ? 'NEXT'
                : current > target
                    ? 'PREVIOUS'
                    : 'CURRENT';

        for (let i = 0; i < steps.length; i++) {
            if (i === step && category === undefined)
                return getStatus(currentIndexes.step, index);

            for (let j = 0; j < steps[i].children.length; j++) {
                index++;
                if (i === step && j === category && section === undefined)
                    return getStatus(currentIndexes.category, index);

                for (let k = 0; k < steps[i].children[j].children.length; k++) {
                    index++;
                    if (i === step && j === category && k === section)
                        return getStatus(currentIndexes.section, index);
                }
            }
            index++;
        }

        return 'NEXT';
    }

    signOut(): void {
        this._router.navigate(['/sign-out']);
    }
}
